import { createAsyncThunk, createSlice, isFulfilled, PayloadAction } from "@reduxjs/toolkit";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { ActualHoursOverviewRequest, ActualHoursOverviewResponse, changeAbsenceHoursModel, changeBatchWeeklyHourse, changeHoursModel, changeHoursResponse, planningDropDownLists, ResorcePlanningSliceModel, resourcePlanningByCostCenterResponse, resourcePlanningByEmployeeResponse, resourcePlanningFilter, resourcePlanningRequest } from "./resource-planning-interface";
import { changeProjectAssignmentResponse, getAllProjectAssignmentResponse, projectAssignmentModel } from "../../Project/state/project-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

const initalValue: ResorcePlanningSliceModel = {
    statusCode: '',
    message: '',
    showNotification: false,
    showAssignmentNotification: false,
    assignmentChange: false,
    isLoading: false,
    isModalLoading: false,
    dropDownLists: { tenants: [], departments: [], costCenters: [], employees: [] },
    resourcePlanningByEmployee: [],
    resourcePlanningByCostCenter: [],
    projectAssignments: [],
    cleanCommitedData: false,
    resourcePlanningFilterByEmployee: null,
    resourcePlanningFilterByCostCenter: null,
    actualHoursData: [],
    holidays: [],
}

export const GetResourcePlanningByEmployee = createAsyncThunk<resourcePlanningByEmployeeResponse, resourcePlanningRequest, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/GetResourcePlanningByEmployee',
    async (data: resourcePlanningRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/resource-planning/overview/employees', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as resourcePlanningByEmployeeResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetResourcePlanningByCostCenter = createAsyncThunk<resourcePlanningByCostCenterResponse, resourcePlanningRequest, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/GetResourcePlanningByCostCenter',
    async (data: resourcePlanningRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/resource-planning/overview/projects', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as resourcePlanningByCostCenterResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPlanningFilterData = createAsyncThunk<planningDropDownLists, void, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/GetPlanningFilterData',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/resource-planning/filter-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as planningDropDownLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateWeeklyAssignedHours = createAsyncThunk<changeHoursResponse, changeHoursModel, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/CreateWeeklyAssignedHours',
    async (model: changeHoursModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/weekly-hours/assign', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as changeHoursResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateWeeklyAssignedHours = createAsyncThunk<changeHoursResponse, changeHoursModel, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/UpdateWeeklyAssignedHours',
    async (model: changeHoursModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/weekly-hours/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as changeHoursResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateBatchWeeklyAssignedHours = createAsyncThunk<changeHoursResponse, changeBatchWeeklyHourse, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/UpdateBatchWeeklyAssignedHours',
    async (model: changeBatchWeeklyHourse, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/weekly-hours/batch/assign', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as changeHoursResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateWeeklyAbsenceHours = createAsyncThunk<changeHoursResponse, changeAbsenceHoursModel, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/CreateWeeklyAbsenceHours',
    async (model: changeAbsenceHoursModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absent-hours/assign', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as changeHoursResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateWeeklyAbsenceHours = createAsyncThunk<changeHoursResponse, changeAbsenceHoursModel, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/UpdateWeeklyAbsenceHours',
    async (model: changeAbsenceHoursModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absent-hours/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as changeHoursResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetAllProjectAssignments = createAsyncThunk<getAllProjectAssignmentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/GetAllProjectAssignments',
    async (projectId: string, { rejectWithValue }) => {
        const response = await fetch(`${baseUri}api/project-assignment/project/${projectId}?current=${false}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as getAllProjectAssignmentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllProjectAssignmentsByEmployee = createAsyncThunk<getAllProjectAssignmentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/GetAllProjectAssignmentsByEmployee',
    async (employeeId: string, { rejectWithValue }) => {
        const response = await fetch(`${baseUri}api/project-assignment/employee/${employeeId}?current=${false}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as getAllProjectAssignmentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateProjectAssignment = createAsyncThunk<changeProjectAssignmentResponse, projectAssignmentModel[], { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/CreateProjectAssignment',
    async (data: projectAssignmentModel[], { rejectWithValue }) => {
        console.log(data);
        const response = await fetch(baseUri + 'api/project-assignment/assign', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as changeProjectAssignmentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateProjectAssignment = createAsyncThunk<changeProjectAssignmentResponse, projectAssignmentModel, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/UpdateProjectAssignment',
    async (data: projectAssignmentModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/project-assignment/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as changeProjectAssignmentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteProjectAssignment = createAsyncThunk<changeProjectAssignmentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/DeleteProjectAssignment',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/project-assignment/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as changeProjectAssignmentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetActualHoursOverview = createAsyncThunk<ActualHoursOverviewResponse, ActualHoursOverviewRequest, { rejectValue: RejectedValue }>(
    'Slice/ResourcePlanningSlice/GetActualHoursOverview',
    async (data: ActualHoursOverviewRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/resource-planning/overview/actual-hours', {
            method: 'POST',
            headers: AuthHeader(),
            body: JSON.stringify(data),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ActualHoursOverviewResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ResourcePlanningSlice = createSlice({
    name: "ResourcePlanningSlice",
    initialState: initalValue,
    reducers: {
        resetRequestSlice: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.showAssignmentNotification = false;
            state.isLoading = false;
            state.resourcePlanningByEmployee = [];
            state.resourcePlanningByCostCenter = [];
            state.projectAssignments = [];
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.showAssignmentNotification = false;
        },
        resetCleanCommitedData: (state) => {
            state.cleanCommitedData = false;
        },
        resetProjectAssignments: (state) => {
            state.projectAssignments = [];
        },
        resetAssignmentChange: (state) => {
            state.assignmentChange = false;
        },
        setResourcePlanningFilterByEmployee: (state, action: PayloadAction<resourcePlanningFilter>) => {
            state.resourcePlanningFilterByEmployee = action.payload;
        },
        setResourcePlanningFilterByCostCenter: (state, action: PayloadAction<resourcePlanningFilter>) => {
            state.resourcePlanningFilterByCostCenter = action.payload;
        },
        resetFilters: (state) => {
            state.resourcePlanningFilterByCostCenter = null;
            state.resourcePlanningFilterByEmployee = null;
        },
        resetActualHoursData: (state) => {
            state.actualHoursData = [];
        },
    },
    extraReducers(builder) {
        //GetResourcePlanningByEmployee
        builder.addCase(GetResourcePlanningByEmployee.fulfilled, (state, action: PayloadAction<resourcePlanningByEmployeeResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.resourcePlanningByEmployee = action.payload.tenantEmployeeResourcePlans;
            state.holidays = action.payload.holidays ?? [];
        })
            .addCase(GetResourcePlanningByEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetResourcePlanningByEmployee.pending, state => {
                state.isLoading = true;
            })
        //GetResourcePlanningByCostCenter
        builder.addCase(GetResourcePlanningByCostCenter.fulfilled, (state, action: PayloadAction<resourcePlanningByCostCenterResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.resourcePlanningByCostCenter = action.payload.projectResources;
        })
            .addCase(GetResourcePlanningByCostCenter.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetResourcePlanningByCostCenter.pending, state => {
                state.isLoading = true;
            })
        //GetFilterData
        builder
            .addCase(GetPlanningFilterData.fulfilled, (state, action: PayloadAction<planningDropDownLists>) => {
                state.isModalLoading = false;
                state.message = '';
                state.dropDownLists = action.payload;
            })
            .addCase(GetPlanningFilterData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPlanningFilterData.pending, state => {
                state.isModalLoading = true;
            })
        //CreateWeeklyHours
        builder.addCase(CreateWeeklyAssignedHours.fulfilled, (state, action: PayloadAction<changeHoursResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.statusCode = '200';
            state.showNotification = true;
        })
            .addCase(CreateWeeklyAssignedHours.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateWeeklyAssignedHours.pending, state => {
                state.isLoading = true;
            })
        //UpdateWeeklyHours
        builder.addCase(UpdateWeeklyAssignedHours.fulfilled, (state, action: PayloadAction<changeHoursResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.statusCode = '200';
            state.showNotification = true;
        })
            .addCase(UpdateWeeklyAssignedHours.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateWeeklyAssignedHours.pending, state => {
                state.isLoading = true;
            })
        //UpdateBatchWeeklyHours
        builder.addCase(UpdateBatchWeeklyAssignedHours.fulfilled, (state, action: PayloadAction<changeHoursResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.statusCode = '200';
            state.showNotification = true;
            state.cleanCommitedData = true;
        })
            .addCase(UpdateBatchWeeklyAssignedHours.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateBatchWeeklyAssignedHours.pending, state => {
                state.isLoading = true;
            })
        //CreateWeeklyAbsenceHours
        builder.addCase(CreateWeeklyAbsenceHours.fulfilled, (state, action: PayloadAction<changeHoursResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.statusCode = '200';
            state.showNotification = true;
        })
            .addCase(CreateWeeklyAbsenceHours.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateWeeklyAbsenceHours.pending, state => {
                state.isLoading = true;
            })
        //UpdateWeeklyAbsenceHours
        builder.addCase(UpdateWeeklyAbsenceHours.fulfilled, (state, action: PayloadAction<changeHoursResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.statusCode = '200';
            state.showNotification = true;
        })
            .addCase(UpdateWeeklyAbsenceHours.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateWeeklyAbsenceHours.pending, state => {
                state.isLoading = true;
            })
        // Get Project Assignment by project
        builder
            .addCase(GetAllProjectAssignments.fulfilled, (state, action: PayloadAction<getAllProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = '';
                state.projectAssignments = action.payload.projectAssignments;
            })
            .addCase(GetAllProjectAssignments.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetAllProjectAssignments.pending, state => {
                state.isModalLoading = true;
            })
        // Get Project Assignment by employee
        builder
            .addCase(GetAllProjectAssignmentsByEmployee.fulfilled, (state, action: PayloadAction<getAllProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = '';
                state.projectAssignments = action.payload.projectAssignments;
            })
            .addCase(GetAllProjectAssignmentsByEmployee.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetAllProjectAssignmentsByEmployee.pending, state => {
                state.isModalLoading = true;
            })
        //Create project assignment
        builder
            .addCase(CreateProjectAssignment.fulfilled, (state, action: PayloadAction<changeProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showAssignmentNotification = true;
                state.statusCode = '200';
                state.assignmentChange = true;
            })
            .addCase(CreateProjectAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showAssignmentNotification = true;
            })
            .addCase(CreateProjectAssignment.pending, state => {
                state.isModalLoading = true;
            })

        //Update Project Assignment
        builder
            .addCase(UpdateProjectAssignment.fulfilled, (state, action: PayloadAction<changeProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showAssignmentNotification = true;
                state.statusCode = '200';
                state.assignmentChange = true;
            })
            .addCase(UpdateProjectAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showAssignmentNotification = true;
            })
            .addCase(UpdateProjectAssignment.pending, state => {
                state.isModalLoading = true;
            })
        //Delete Project Assignment
        builder
            .addCase(DeleteProjectAssignment.fulfilled, (state, action: PayloadAction<changeProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showAssignmentNotification = true;
                state.statusCode = '200';
                state.assignmentChange = true;
            })
            .addCase(DeleteProjectAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showAssignmentNotification = true;
            })
            .addCase(DeleteProjectAssignment.pending, state => {
                state.isModalLoading = true;
            })
        //GetActualHoursOverview
        builder
            .addCase(GetActualHoursOverview.fulfilled, (state, action: PayloadAction<ActualHoursOverviewResponse>) => {
                state.actualHoursData = action.payload.entries;
            })
            .addCase(GetActualHoursOverview.rejected, (state) => {
                state.actualHoursData = [];
            })
            .addCase(GetActualHoursOverview.pending, state => {
                state.actualHoursData = [];
            })
    }
})

export const { resetRequestSlice, resetStatusCode, resetCleanCommitedData, resetProjectAssignments,
    setResourcePlanningFilterByCostCenter, setResourcePlanningFilterByEmployee, resetFilters, resetAssignmentChange,
    resetActualHoursData } = ResourcePlanningSlice.actions;
export default ResourcePlanningSlice.reducer;